import { useEffect, useState, useRef } from "react";
import { Sun, Moon, Monitor, Check } from "lucide-react";

const STORAGE_KEY = "signal-edge:theme";
const DEFAULT_PREF = "dark";
const OPTIONS = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

function readPref() {
  if (typeof window === "undefined") return DEFAULT_PREF;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : DEFAULT_PREF;
  } catch {
    return DEFAULT_PREF;
  }
}

function resolveApplied(pref) {
  if (pref === "system" && typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return pref === "light" ? "light" : "dark";
}

function applyPref(pref) {
  if (typeof document === "undefined") return;
  const applied = resolveApplied(pref);
  document.documentElement.setAttribute("data-theme", applied);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", applied === "light" ? "#f8fafc" : "#0b1120");
  }
}

export default function ThemeToggle({ className = "" }) {
  const [pref, setPref] = useState(readPref);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Apply preference + persist
  useEffect(() => {
    applyPref(pref);
    try {
      window.localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      /* ignore quota errors */
    }
  }, [pref]);

  // When preference is 'system', react to OS-level changes
  useEffect(() => {
    if (pref !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyPref("system");
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [pref]);

  // Close dropdown on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = OPTIONS.find((o) => o.value === pref) || OPTIONS[1];
  const CurrentIcon = current.Icon;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="theme-toggle"
        data-theme-pref={pref}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Theme: ${current.label}. Click to change.`}
        title={`Theme: ${current.label}`}
        className={`flex h-9 w-9 shrink-0 items-center justify-center border border-[#1e293b] text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc] ${className}`}
      >
        <CurrentIcon className="h-4 w-4" />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          data-testid="theme-menu"
          className="absolute right-0 top-full z-40 mt-1 w-40 border border-[#1e293b] bg-[#111827] py-1 shadow-lg"
        >
          {OPTIONS.map(({ value, label, Icon }) => {
            const selected = pref === value;
            return (
              <button
                key={value}
                role="menuitemradio"
                aria-checked={selected}
                type="button"
                onClick={() => {
                  setPref(value);
                  setOpen(false);
                }}
                data-testid={`theme-option-${value}`}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  selected
                    ? "bg-[#0f172a] text-[#f8fafc]"
                    : "text-[#94a3b8] hover:bg-[#0f172a] hover:text-[#f8fafc]"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 text-left">{label}</span>
                {selected && <Check className="h-3.5 w-3.5 text-[#3b82f6]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
